import type { WorkItemAttachment } from '@captain-tasko/shared';
import { Paperclip, FileText, Image, FileSpreadsheet, FileCode, File as FileIcon } from 'lucide-react';

// 0.1.52 — CTSK-167 Issue A: workitem detail was showing NO surface for
// the workitem-level attachments (`data.attachments[]` from the 0.1.46
// API split). Panel renders read-only, one row per attachment, with:
//   - mime-type-derived icon (FileText for md/txt, Image for image/*,
//     FileSpreadsheet for csv/xlsx, FileCode for json/xml, generic
//     FileIcon otherwise)
//   - clickable title → navigates to the doc detail
//   - size (KB / MB, human-formatted)
//   - source tag chip (session-attribution — CTSK-154 parity, 0.1.46)
//   - attach-time as a relative timestamp
//
// This is intentionally add-less for now — attaching happens via the
// comment textarea's file drop / paste (see 0.1.46 Phase F) or via
// tasko-mcp attach_text. If the caller has zero attachments, the whole
// panel is suppressed so we don't add empty chrome.

function iconForMime(mime: string) {
  if (mime.startsWith('image/')) return Image;
  if (mime === 'text/markdown' || mime === 'text/plain') return FileText;
  if (mime === 'text/csv' || mime.includes('spreadsheet') || mime.includes('excel')) return FileSpreadsheet;
  if (mime === 'application/json' || mime === 'text/xml' || mime === 'application/xml') return FileCode;
  return FileIcon;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function AttachmentsPanel({
  attachments,
  onNavigate,
}: {
  attachments: WorkItemAttachment[];
  onNavigate: (p: string) => void;
}) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div
      id="attachments"
      style={{
        background: 'var(--surface-raised)',
        padding: 16,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Paperclip size={14} aria-hidden="true" />
          Attachments
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
            ({attachments.length})
          </span>
        </h4>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {attachments.map((a) => {
          const Icon = iconForMime(a.mimeType);
          return (
            <div
              key={a.id}
              style={{
                padding: '6px 8px',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon size={16} aria-hidden="true" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <a
                  href={`/documents/${a.id}`}
                  onClick={(e) => { e.preventDefault(); onNavigate(`/documents/${a.id}`); }}
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                  }}
                  title={a.title}
                >
                  {a.title}
                </a>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>{a.mimeType}</span>
                  <span>·</span>
                  <span>{formatSize(a.sizeBytes)}</span>
                  <span>·</span>
                  <span title={a.createdAt}>{formatRelative(a.createdAt)}</span>
                  {a.sourceTag && (
                    <>
                      <span>·</span>
                      <span
                        title="Session that attached this file (source tag)"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          background: 'var(--surface-sunken)',
                          padding: '0 4px',
                          borderRadius: 3,
                        }}
                      >
                        {a.sourceTag}
                      </span>
                    </>
                  )}
                </div>
                {a.note && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    “{a.note}”
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
